import { getChainContracts } from "@/shared/config/blockchain/helpers";
import { useWeb3State } from "@/web3/context";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import useCollectionContract from "../../Collection/useCollectionContract";
import useMutationWithHandler from "../../common/hooks/useMutationWithHandler";
import { MutationArgs } from "../../common/types";
import useApprovalPrefetch from "./useApprovalPrefetch";

export const useOrderApprovalMutation = (
  nftContract?: string,
  { onError }: MutationArgs = {},
) => {
  const { currentAddress, chainId } = useWeb3State();

  const collectionContract = useCollectionContract(nftContract);

  const queryClient = useQueryClient();

  const { isApproved, isLoading } = useApprovalPrefetch(nftContract);

  const { mutateAsync: approveAll, isLoading: isApproveAllLoading } =
    useMutationWithHandler(
      async () => {
        const contracts = getChainContracts(chainId);

        const tx = await collectionContract!.setApprovalForAll(
          contracts!.exchangeAddress,
          true,
        );

        return await tx.wait();
      },
      {
        onSuccess: () =>
          queryClient.invalidateQueries([
            "order-isApproved",
            currentAddress,
            nftContract,
          ]),
        onError,
        rethrowError: true,
      },
      false,
    );

  const approveWithCheck: () => Promise<boolean> = useCallback(async () => {
    if (!isApproved) {
      const isApproveAllSuccess = await approveAll();

      if (!isApproveAllSuccess) {
        return false;
      }
    }

    return true;
  }, [isApproved, approveAll]);

  return {
    isApprovalLoading: isLoading || isApproveAllLoading,
    approveAll: approveWithCheck,
  };
};
